<template>
  <div class="sticky-container">
    <v-layout fill-height>
      <v-container py-0>
        <div class="chart-container">
          <h3 class="font-weight-bold mb-5">VOTING PERCENTAGES OF EACH COUNTRY AMONG THE MAJOR INTERNATIONAL BANKS</h3>

          <div class="svg-container">
            <!-- tooltip -->
            <v-card class="card-tooltip">
              <v-card-text class="caption">
                <h4 class="font-weight-bold">{{ currentCountry.name }}</h4>

                <p class="mb-0">{{ currentCountry.votingPower }}%</p>
              </v-card-text>
            </v-card>
          </div>
        </div>
      </v-container>
    </v-layout>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import * as d3 from 'd3';
import _kebabCase from 'lodash.kebabcase';

export default {
  name: 'D3BarChart',
  data() {
    return {
      gChart: null,
      chartSettings: {
        margin: { top: 0, right: 0, bottom: 0, left: 40 },
        width: 0,
        height: 0,
      },
      chartScale: {},
      banksData: [],
      currentCountry: {},
    };
  },
  computed: {
    ...mapGetters('view', ['step']),
  },
  watch: {
    step(newVal) {
      this.handleChart(newVal);
    },
  },
  async mounted() {
    // fetch csv data
    const AIIB = await d3.csv('/data/finance/aiib.csv');
    const IMF = await d3.csv('/data/finance/imf.csv');
    const IBRD = await d3.csv('/data/finance/ibrd.csv');
    const ADB = await d3.csv('/data/finance/adb.csv');

    // format datasets
    this.banksData[0] = { name: 'AIIB', countries: [...this.formatRawData(AIIB)] };
    this.banksData[1] = { name: 'IMF', countries: [...this.formatRawData(IMF)] };
    this.banksData[2] = { name: 'IBRD', countries: [...this.formatRawData(IBRD)] };
    this.banksData[3] = { name: 'ADB', countries: [...this.formatRawData(ADB)] };

    // sort data
    this.sortData(this.banksData);

    // fade in component
    // create transition
    const chartFadeIn = d3
      .transition()
      .duration(1000)
      .ease(d3.easeLinear);

    d3.select('.chart-container')
      .transition(chartFadeIn)
      .style('opacity', 1);

    // draw svg
    this.drawSVG();

    // create scales and y-axis
    this.createScales();

    // draw chart
    this.drawBarChart();
  },
  methods: {
    formatRawData2(data) {
      // format data into array of - { name: 'Asia', countries: [], votingPercentage: 0, }
      const groupByRegions = [
        { name: 'Asia', countries: [], votingPercentage: 0 },
        { name: 'Africa', countries: [], votingPercentage: 0 },
        { name: 'Europe', countries: [], votingPercentage: 0 },
        { name: 'North America', countries: [], votingPercentage: 0 },
        { name: 'Oceania', countries: [], votingPercentage: 0 },
        { name: 'South America', countries: [], votingPercentage: 0 },
      ];

      // loop through csv data
      data.forEach(country => {
        // convert country.size to number
        country.size = country.size * 1;

        groupByRegions.map(region => {
          if (country.region === region.name) {
            // total voting power
            // add country data
            region.votingPercentage += country.size;
            region.countries.push(country);
          }
        });
      });

      return groupByRegions;
    },
    formatRawData(data) {
      // format data
      data = data.map(({ name, size, region }) => {
        return {
          name,
          votingPower: size * 1, // change key name and data type
          region,
        };
      });

      return data;
    },
    sortData(data) {
      // sort countries of each banks by votingPower descending
      data.forEach(bank => {
        bank.countries.sort((a, b) => b.votingPower - a.votingPower);
      });

      return data;
    },
    drawSVG() {
      // set dimensions
      this.chartSettings.width =
        this.$el.clientWidth - 48 - this.chartSettings.margin.left - this.chartSettings.margin.right;

      this.chartSettings.height =
        this.$el.clientHeight * 0.3 - this.chartSettings.margin.top - this.chartSettings.margin.bottom;

      // draw SVG
      this.gChart = d3
        .select('.svg-container')
        .append('svg')
        .attr('width', this.$el.clientWidth - 48)
        .attr('height', this.$el.clientHeight * 0.3)
        .call(this.responsivefy) // takes care of responsive d3
        .append('g')
        .attr('class', 'g-chart')
        .attr('transform', `translate(${this.chartSettings.margin.left}, ${this.chartSettings.margin.top})`);
    },
    responsivefy(svg) {
      // container will be the DOM element that the svg is appended to
      // we then measure the container and find its aspect ratio
      const width = parseInt(svg.style('width'), 10);
      const height = parseInt(svg.style('height'), 10);
      const aspect = width / height;

      // set viewBox attribute to the initial size
      // control scaling with preserveAspectRatio
      // resize svg on inital page load
      svg
        .attr('viewBox', `0 0 ${width} ${height}`)
        .attr('preserveAspectRatio', 'xMinYMid')
        .call(this.resize, aspect);

      // add a listener so the chart will be resized
      // when the window resizes
      // multiple listeners for the same event type
      // requires a namespace, i.e., 'click.foo'
      // api docs: https://goo.gl/F3ZCFr
      d3.select(window).on('resize.test', () => {
        this.resize(svg, aspect);
      });
    },
    resize(svg, aspect) {
      // this is the code that resizes the chart
      // it will be called on load
      // and in response to window resizes gets the width of the container
      // and resizes the svg to fill it while maintaining a consistent aspect ratio
      const container = d3.select(svg.node().parentNode);
      const w = parseInt(container.style('width'), 10);

      svg.attr('width', w);
      svg.attr('height', Math.round(w / aspect));
    },
    createScales() {
      // create scales
      this.chartScale.y = d3
        .scaleBand()
        .domain(this.banksData.map(d => d.name))
        .rangeRound([0, this.chartSettings.height])
        .paddingInner(0.2);

      this.chartScale.x = d3
        .scaleLinear()
        .domain([0, 100])
        .rangeRound([0, this.chartSettings.width]);

      const yAxis = d3
        .axisLeft()
        .tickSize(0)
        .tickPadding(10)
        .scale(this.chartScale.y);

      // draw y axis
      this.gChart
        .append('g')
        .attr('class', 'axis y-axis')
        .call(yAxis);
    },
    drawBarChart() {
      // draw bars
      const gBanks = this.gChart
        .selectAll('.banks')
        .data(this.banksData.map(d => d.name))
        .enter()
        .append('g')
        .attr('class', name => `banks bank-${name}`)
        .classed('fill-none', name => name !== 'AIIB' && this.step === '4-2')
        .attr('transform', name => `translate(0, ${this.chartScale.y(name)})`);

      // loop through each banks
      gBanks.each((bank, i, gBanksArr) => {
        let totalCountryPercentage = 0;

        // populate with countries
        const gCountries = d3
          .select(gBanksArr[i])
          .selectAll('g')
          .data(this.banksData.find(d => d.name === bank).countries)
          .enter()
          .append('g')
          .attr('class', country => `country ${_kebabCase(country.name)}`)
          .on('mouseenter', this.handleToolTipShow);
        // .on('mouseleave', this.handleToolTipHide);

        // draw rects
        gCountries
          .append('rect')
          .attr(
            'width',
            country => (this.chartScale.x(country.votingPower) === 0 ? 1 : this.chartScale.x(country.votingPower))
          )
          .attr('height', this.chartScale.y.bandwidth());

        // draw texts
        const gCountriesText = gCountries
          .append('g')
          .attr('class', 'country__text')
          .attr('transform', () => `translate(10, ${this.chartScale.y.bandwidth() * 0.5})`);

        const countryText = gCountriesText.append('text').attr('text-anchor', 'start');

        countryText
          .append('tspan')
          .attr('class', 'country-text__name font-weight-bold')
          .attr('dy', '-0.2em')
          .text(country => country.name);

        countryText
          .append('tspan')
          .attr('class', 'country-text__power')
          .attr('x', 0)
          .attr('dy', '1.5em')
          .text(country => `${country.votingPower}%`);

        // set pos x of each country elements
        gCountries.attr('transform', (country, i) => {
          totalCountryPercentage = i === 0 ? 0 : totalCountryPercentage + gCountries.data()[i - 1].votingPower;

          return `translate(${this.chartScale.x(totalCountryPercentage)}, 0)`;
        });

        const chartScaleX = this.chartScale.x;

        gCountriesText
          .filter(function(country) {
            return this.getBBox().width + 10 >= chartScaleX(country.votingPower);
          })
          .remove();
      });
    },
    drawBarChart2() {
      // create scales
      this.chartScale.y = d3
        .scaleBand()
        .domain(this.banksData.map(d => d.name))
        .rangeRound([0, this.$el.clientHeight * 0.3]);

      this.chartScale.x = d3
        .scaleLinear()
        .domain([0, 100])
        .rangeRound([0, this.$el.clientWidth]);

      // set region's color
      const regionColors = ['#F16053', '#196666', '#52A08D', '#4940AC', '#867CFF', '#87D8A9'];

      // draw bars
      const gBars = this.gChart
        .selectAll('.bars')
        .data(this.banksData.map(d => d.name))
        .enter()
        .append('g')
        .attr('class', name => `bars bank-${name}`)
        .attr('transform', name => `translate(0, ${this.chartScale.y(name)})`);

      gBars.each(bank => {
        const bankData = this.banksData.find(d => d.name == bank);
        let totalPercentage = 0;

        const gRegions = d3
          .select(`.bank-${bank}`)
          .selectAll('g')
          .data(bankData.regions)
          .enter()
          .append('g')
          .attr('class', (region, i) => `region region-${i + 1}`)
          .attr('transform', region => {
            const test = totalPercentage;
            totalPercentage += region.votingPercentage;

            return `translate(${this.chartScale.x(test)}, 0)`;
          });

        // loop through region groups
        gRegions.each((d, i, gRegion) => {
          let totalCountryPercentage = 0;

          const countries = d3
            .select(gRegion[i])
            .attr('fill', () => regionColors[i])
            .selectAll('rect')
            .data(bankData.regions[i].countries)
            .enter()
            .append('rect')
            .attr('class', c => c.name)
            .attr('width', country => this.chartScale.x(country.size))
            .attr('height', this.chartScale.y.bandwidth())
            .attr('stroke', '#fff');

          countries.attr('x', (country, i) => {
            totalCountryPercentage = i === 0 ? 0 : totalCountryPercentage + countries.data()[i - 1].size;

            return this.chartScale.x(totalCountryPercentage);
          });
        });

        // gRegions
        //   .selectAll('rect')
        //   .data()
        //   .enter()
        //   .append('rect')
        //   .attr('width', region => this.chartScale.x(region.votingPercentage))
        //   .attr('height', 20)
        //   .attr('fill', (d, i) => regionColors[i])
        //   .attr('stroke', '#fff');
      });

      // create stack
      // let stack = d3.stack()
      //   .keys(['asia', 'africa', 'europe', 'northAmerica', 'oceania', 'southAmerica'])
      //   .order(d3.stackOrderNone)
      //   .offset(d3.stackOffsetNone);

      // let stackedSeries = stack(this.banksData);

      // console.log(stackedSeries);

      // // draw bars
      // const gBars = this.gChart
      //   .selectAll('.bars')
      //   .data(stackedSeries)
      //   .enter()
      //   .append('g')
      //   .attr('class', d => `bars region-${d.key}`)
      //   .attr('fill', (d, i) => regionColors[i]);

      // gBars.selectAll('rect')
      //   .data(d => d)
      //   .enter()
      //   .append('rect')
      //   .attr('y', d => this.chartScale.y(d.data.name))
      //   .attr('x', d => this.chartScale.x(d[0]))
      //   .attr('width', d => this.chartScale.x(d[1]) - this.chartScale.x(d[0]))
      //   .attr('height', d => this.chartScale.y.bandwidth());
    },
    handleToolTipShow(d) {
      this.currentCountry = d;

      const tooltip = d3.select('.card-tooltip');
      const tooltipWidth = parseFloat(tooltip.style('width'));
      const tooltipHeight = parseFloat(tooltip.style('height'));

      // get starting pos x - yaxis width + g transform value
      const yAxisWidth = d3
        .select('.y-axis')
        .node()
        .getBBox().width;
      const targetTransform = d3.event.target.attributes.transform.value;
      const transformX = targetTransform
        .split('(')[1]
        .split(')')[0]
        .split(',')[0];

      const posX =
        +transformX + tooltipWidth > this.chartScale.x(100)
          ? this.chartScale.x(100) - tooltipWidth
          : yAxisWidth + +transformX + d3.event.target.getBBox().width * 0.5;

      // get pos y - parent g class name - bank-???
      const targetParentClass = d3.event.target.parentNode.attributes.class.value;
      const bankName = targetParentClass.split(' ')[1].split('-')[1];
      const posY = this.chartScale.y(bankName) - tooltipHeight;

      // move tooltip
      tooltip
        .style('left', `${posX - 5}px`) // 5px from y-axis tickPadding
        .style('top', `${posY}px`)
        .style('transform', 'translate(-50%, 0)');

      // create transition
      const tooltipTransit = d3
        .transition()
        .duration(250)
        .ease(d3.easeLinear);

      // fade tooltip in
      tooltip.transition(tooltipTransit).style('opacity', 1);
    },
    handleToolTipHide() {
      // hide tooltip
      const tooltip = d3.select('.card-tooltip');

      // create transition
      const tooltipTransit = d3
        .transition()
        .duration(500)
        .ease(d3.easeLinear);

      // fade tooltip out
      tooltip
        .transition(tooltipTransit)
        .style('opacity', 0)
        .style('top', 0)
        .style('left', 0);
    },
    handleChart(currentStep) {
      switch (currentStep) {
        case '4-2':
          d3.select('.g-chart')
            .selectAll('.banks')
            .classed('fill-china', false);

          d3.select('.g-chart')
            .select('.bank-AIIB')
            .classed('fill-none', false);
          break;

        case '4-2-1':
          d3.select('.g-chart')
            .selectAll('.banks')
            .classed('fill-none', true)
            .classed('fill-china', true);
          break;

        case '4-2-2':
          d3.select('.g-chart')
            .selectAll('.banks')
            .classed('fill-none', false)
            .classed('fill-china', false);
          break;

        default:
          break;
      }
    },
  },
};
</script>

<style lang="stylus" scoped>
.sticky-container {
  height: 100%;

  .container {
    max-width: none
  }

  .chart-container {
    opacity: 0;

    .svg-container {
      position: relative;

      .card-tooltip {
        position: absolute;
        opacity: 0;
      }

      >>> .g-chart {
        .axis {
          font-size: 15px;
          font-weight: 700;
          font-family: 'LL Akkurat Pro', Helvetica, sans-serif;

          path.domain {
            display: none;
          }
        }

        .banks {
          text {
            fill: #000;

            .country-text__name {
              font-size: 12px;
            }

            .country-text__power {
              font-size: 11px;
            }
          }

          rect {
            stroke-width: 0.5;
          }

          &.fill-none {
            rect {
              fill: #E6E1D9;
              stroke: #D1C1A4;
            }
          }

          &.fill-china {
            .country.china {
              text {
                fill: #fff;
              }

              rect {
                fill: #F16053;
              }
            }
          }
        }

        .bank-AIIB {
          rect {
            fill: #F1B2A8;
            stroke: #F61400;
          }
        }

        .bank-IMF {
          rect {
            fill: #C0BDEC;
            stroke: #5C50D8;
          }
        }

        .bank-IBRD {
          rect {
            fill: #A7D1C8;
            stroke: #1C9781;
          }
        }

        .bank-ADB {
          rect {
            fill: #7975A5;
            stroke: #2D2679;
          }
        }
      }

    }
  }
}
</style>
